"use client";
import { LandingCenterBranchItemApiModel } from "@/Apis/ClinicApi";
import useLandingAppointmentModal from "@/Areas/Landing/Pages/LandingAppointment/useLandingAppointmentModal";
import { FavoriteToggle, If, SmartImage, SmartLink } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useT } from "@/Hooks";
import { Icon } from "@iconify/react";
import { Button, Flex, Space, Tooltip, Typography } from "antd";
import _ from "lodash";
import { FC } from "react";
import { BsHeart, BsHeartFill } from "react-icons/bs";
import { FaStar } from "react-icons/fa";
import { LuCalendar, LuMapPin } from "react-icons/lu";
import "./CenterBranchItem.css";

const { Text, Paragraph } = Typography;

export type CenterBranchItemDisplayMode = "grid" | "list";
interface CenterBranchItemProps {
  model: LandingCenterBranchItemApiModel;
  displayMode?: CenterBranchItemDisplayMode;
}

const CenterBranchItem: FC<CenterBranchItemProps> = ({
  model,
  displayMode = "grid",
}) => {
  const t = useT();
  const departments = model.Departments.map((d) => d.Name);
  const firstDepartment = departments[0];
  const restDepartments = departments.slice(1);
  const landingAppointmentModal = useLandingAppointmentModal();
  const socialNetworks = model.CenterSocialNetworks;
  if (displayMode == "grid")
    return (
      <div className="card">
        <div className="card-img card-img-hover">
          <SmartLink href={model.Link}>
            <SmartImage
              cover
              src={model.ImageUrl}
              alt={model.Name}
              width={304}
              height={195}
              objectFit="contain"
            />
          </SmartLink>
          <div className="grid-overlay-item d-flex align-items-center justify-content-between">
            <div>
              {model.PointsAvg && (
                <span className="badge bg-orange">
                  {/* <i className="fa-solid fa-star me-1" /> */}
                  <FaStar className="me-1" />
                  {model.PointsAvg}
                </span>
              )}
            </div>
            <FavoriteToggle
              ids={{
                type: CenterOptions.Enum.UserFavoriteType.Center.key,
                centerId: model.CenterId,
              }}
              defaultFavorite={model.IsFavorited}
            >
              {({ isFavorite, loading, toggle }) => (
                <span
                  className={`fav-icon ${isFavorite ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault(); // نذار لینک کلیک بشه
                    e.stopPropagation(); // نذار کلیک به والدها برسه
                    toggle();
                  }}
                >
                  {loading ? (
                    <Icon icon="eos-icons:loading" width="18" height="18" />
                  ) : isFavorite ? (
                    <BsHeartFill />
                  ) : (
                    <BsHeart />
                  )}
                </span>
              )}
            </FavoriteToggle>
          </div>
        </div>
        <div className="card-body p-0">
          <Flex
            align="center"
            justify="space-between"
            gap={8}
            className="active-bar p-3"
          >
            <Tooltip
              title={
                restDepartments.length ? departments.join("، ") : undefined
              }
            >
              <Typography.Text
                className="text-indigo fw-medium fs-14"
                ellipsis
                style={{ maxWidth: 180 }}
              >
                {firstDepartment}
                {restDepartments.length > 0 && " …"}
              </Typography.Text>
            </Tooltip>
            {/* <div style={{ width: 25 }}>
                            <If condition={model.BlueTick}>
                                <MdVerified color="#1DA1F2" size={18} />
                            </If>
                        </div> */}
            {/* <span className="badge bg-info-light d-inline-flex align-items-center">

            <i className="fa-solid fa-circle fs-5 me-1" />
            Available
        </span> */}
          </Flex>
          <div className="p-3 pt-0">
            <div className="doctor-info-detail mb-3 pb-3">
              <h3 className="mb-1">
                <SmartLink href={model.Link}>{model.Name}</SmartLink>
              </h3>
              <div className="d-flex align-items-center">
                {/*{model.Address && <p className="d-flex align-items-center mb-0 fs-14">
                                    <LuMapPin className="me-2" size={16} />
                                    {model.Address}
                                </p>}*/}
                {/* <i className="fa-solid fa-circle fs-5 text-primary mx-2 me-1" /> */}
                {/* <span className="fs-14 fw-medium">30 Min</span> */}

                <Space orientation="vertical">
                  {/* <CenterBranchTag model={model.CenterBranch} /> */}
                  {model.BriefDescription && (
                    <p className="d-flex align-items-center mb-0 fs-14">
                      <Paragraph
                        ellipsis={{ tooltip: model.BriefDescription, rows: 2 }}
                        // style={{ maxWidth: 150 }}
                      >
                        {model.BriefDescription}
                      </Paragraph>
                    </p>
                  )}
                  <If condition={!_.isEmpty(socialNetworks)}>
                    <Space wrap>
                      {socialNetworks.map((socialNetwork) => (
                        <SmartLink
                          key={socialNetwork.Id}
                          href={socialNetwork.Link}
                          title={socialNetwork.Name}
                        >
                          <Button
                            shape="circle"
                            icon={<Icon icon={socialNetwork.Icon} />}
                          />
                        </SmartLink>
                      ))}
                    </Space>
                  </If>
                </Space>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                {model.Address && (
                  <p className="d-flex align-items-center mb-0 fs-14">
                    <LuMapPin className="me-2" size={16} />
                    <Paragraph
                      ellipsis={{ tooltip: model.Address, rows: 2 }}
                      style={{ maxWidth: 150 }}
                    >
                      {model.Address}
                    </Paragraph>
                  </p>
                )}
              </div>
              <If condition={model.HasInternetSchedulingModule}>
                <button
                  className="btn btn-md btn-dark d-inline-flex align-items-center rounded-pill text-truncate"
                  onClick={async () => {
                    await landingAppointmentModal.showBooking({
                      values: {
                        CenterId: model.CenterId,
                        CenterBranchId: model.Id,
                      },
                    });
                  }}
                >
                  <LuCalendar className="me-2" size={18} />
                  {t(
                    "landing.centerBranchItem.bookAppointment",
                    undefined,
                    "Book appointment",
                  )}
                </button>
              </If>
            </div>
          </div>
        </div>
      </div>
    );
  return (
    <div className="card doctor-list-card">
      <div className="d-md-flex align-items-center">
        <div className="card-img card-img-hover">
          <SmartLink href={model.Link}>
            <SmartImage
              cover
              src={model.ImageUrl}
              alt={model.Name}
              width={300}
              height={253}
              objectFit="contain"
            />
          </SmartLink>
          <div className="grid-overlay-item d-flex align-items-center justify-content-between">
            <div>
              {model.PointsAvg && (
                <span className="badge bg-orange">
                  {/* <i className="fa-solid fa-star me-1" /> */}
                  <FaStar className="me-1" />
                  {model.PointsAvg}
                </span>
              )}
            </div>
            <FavoriteToggle
              ids={{
                type: CenterOptions.Enum.UserFavoriteType.Center.key,
                centerId: model.CenterId,
              }}
              defaultFavorite={model.IsFavorited}
            >
              {({ isFavorite, loading, toggle }) => (
                <span
                  className={`fav-icon ${isFavorite ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault(); // نذار لینک کلیک بشه
                    e.stopPropagation(); // نذار کلیک به والدها برسه
                    toggle();
                  }}
                >
                  {loading ? (
                    <Icon icon="eos-icons:loading" width="18" height="18" />
                  ) : isFavorite ? (
                    <BsHeartFill />
                  ) : (
                    <BsHeart />
                  )}
                </span>
              )}
            </FavoriteToggle>
          </div>
        </div>
        <div className="card-body p-0">
          {/* <div className="d-flex align-items-center justify-content-between border-bottom p-3">
          <SmartLink href="#" className="text-teal fw-medium fs-14">
            Neurologist
          </SmartLink>
          <span className="badge bg-success-light d-inline-flex align-items-center">
            <i className="fa-solid fa-circle fs-5 me-1" />
            Available
          </span>
        </div> */}
          <Flex
            align="center"
            justify="space-between"
            gap={8}
            className="border-bottom p-3"
          >
            <Tooltip
              title={
                restDepartments.length ? departments.join("، ") : undefined
              }
            >
              <Typography.Text
                className="text-indigo fw-medium fs-14"
                ellipsis
                style={{ maxWidth: 300 }}
              >
                {departments.join("، ")}
              </Typography.Text>
            </Tooltip>
            {/* <div style={{ width: 25 }}>
                        <If condition={model.BlueTick}>
                            <MdVerified color="#1DA1F2" size={18} />
                        </If>
                    </div> */}
            {/* <span className="badge bg-info-light d-inline-flex align-items-center">

            <i className="fa-solid fa-circle fs-5 me-1" />
            Available
        </span> */}
          </Flex>
          <div className="p-3">
            <div className="doctor-info-detail pb-3">
              <div className="row align-items-center gy-3">
                <div className="col-sm-6">
                  <div>
                    <h6 className="d-flex align-items-center mb-1">
                      <SmartLink href={model.Link}>{model.Name}</SmartLink>
                      {/* <i className="isax isax-tick-circle5 text-success ms-2" /> */}
                    </h6>
                    {/* <p className="mb-2">MBBS, DNB - Neurology</p> */}
                    {model.Address && (
                      <p className="d-flex align-items-center mb-0 fs-14">
                        <LuMapPin className="me-2" size={16} />
                        {model.Address}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-sm-6">
                  <div>
                    <Space orientation="vertical">
                      {/* <CenterBranchTag model={model.CenterBranch} /> */}
                      <If condition={!_.isEmpty(socialNetworks)}>
                        <Space wrap>
                          {socialNetworks.map((socialNetwork) => (
                            <SmartLink
                              key={socialNetwork.Id}
                              href={socialNetwork.Link}
                              title={socialNetwork.Name}
                            >
                              <Button
                                shape="circle"
                                icon={<Icon icon={socialNetwork.Icon} />}
                              />
                            </SmartLink>
                          ))}
                        </Space>
                      </If>
                      {model.BriefDescription && (
                        <p className="d-flex align-items-center mb-0 fs-14">
                          <Paragraph
                            ellipsis={{
                              tooltip: model.BriefDescription,
                              rows: 2,
                            }}
                            style={{ maxWidth: 150 }}
                          >
                            {model.BriefDescription}
                          </Paragraph>
                        </p>
                      )}
                    </Space>
                    {/* <p className="d-flex align-items-center mb-0 fs-14 mb-2">
                    <i className="isax isax-language-circle text-dark me-2" />
                    English, French
                  </p>
                  <p className="d-flex align-items-center mb-0 fs-14 mb-2">
                    <i className="isax isax-like-1 text-dark me-2" />
                    98% (252 / 287 Votes)
                  </p>
                  <p className="d-flex align-items-center mb-0 fs-14">
                    <i className="isax isax-archive-14 text-dark me-2" />
                    20 Years of Experience
                  </p>*/}
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3 mt-3">
              <div className="d-flex align-items-center flex-wrap row-gap-3">
                <div className="me-3">
                  {/*   {model.Address && <p className="d-flex align-items-center mb-0 fs-14">
                                    <LuMapPin className="me-2" size={16} />
                                    {model.Address}
                                </p>}*/}
                </div>
                {/* {model.FirstFreeAppointmentDateTime && (
                                <p className="mb-0">
                                    اولین نوبت در دسترس <br />
                                    {dayjs(model.FirstFreeAppointmentDateTime)
                                        .format("HH:mm - DD MMMM، ddd")}
                                </p>
                            )} */}
              </div>
              <button
                className="btn btn-md btn-primary-gradient d-inline-flex align-items-center rounded-pill"
                onClick={async () => {
                  await landingAppointmentModal.showBooking({
                    values: {
                      CenterId: model.CenterId,
                      CenterBranchId: model.Id,
                    },
                  });
                }}
              >
                <LuCalendar className="me-2" size={18} />
                {t(
                  "landing.centerBranchItem.bookAppointment",
                  undefined,
                  "Book appointment",
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CenterBranchItem;
